import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, cdnImg } from "@/lib/api";

const cleanTitle = (t) => (t || "").replace(/^\s*#?\d+\s*[-–:]\s*/, "");

/** "Selling right now" — slow-scrolling rail of the last ~50 REAL sales
 *  (social_proof feed from Shopify order webhooks). One card per product,
 *  most recent purchase; items without a photo/link are skipped. */
export const RecentBuys = () => {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    api.get("/social-proof?limit=50").then((r) => setOrders(r.data.orders || [])).catch(() => setOrders([]));
  }, []);

  const cards = useMemo(() => {
    if (!orders) return [];
    const seen = new Set();
    const out = [];
    // Every actual sold item gets a card (multi-piece orders show each piece);
    // dedupe by slug only guards the rare re-stocked piece selling twice.
    for (const o of orders) {
      for (const item of o.items || []) {
        if (!item.image || !item.slug || seen.has(item.slug)) continue;
        seen.add(item.slug);
        const where = o.city ? `${o.city}${o.province ? `, ${o.province}` : ""}` : "Chicago, Illinois";
        out.push({
          slug: item.slug,
          image: item.image,
          title: cleanTitle(item.title),
          meta: [where, "Just sold"].filter(Boolean).join(" · "),
          soldOut: item.in_stock === false,
        });
        if (out.length >= 50) return out;
      }
    }
    return out;
  }, [orders]);

  if (!cards.length || cards.length < 4) return null;

  return (
    <section className="py-10 border-t border-[#E5E5DF]" data-testid="recent-buys">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2.5 mb-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7A8B6F] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#7A8B6F]" />
            </span>
            <span className="overline text-[#7A8B6F]">Live from the boutique</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Selling right now</h2>
          <p className="text-sm text-[#6E6E69] mt-1">Real pieces our shoppers just picked up — online &amp; in-store</p>
        </div>
        <span className="text-[0.68rem] font-mono tracking-[0.16em] uppercase text-[#6E6E69]" data-testid="recent-buys-count">
          See what others are buying
        </span>
      </div>

      <div className="recent-buys-wrap overflow-hidden">
        <div className="recent-buys-track flex gap-4 w-max px-5" data-testid="recent-buys-track">
          {[...cards, ...cards].map((c, i) => (
            <Link
              key={`${c.slug}-${i}`}
              to={`/product/${c.slug}`}
              data-testid={`recent-buy-card-${i}`}
              className="group w-36 sm:w-40 shrink-0"
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-[#EFEFEA] border border-[#E5E5DF] group-hover:border-[#B84A3B] transition-colors duration-300">
                <img
                  src={cdnImg(c.image, 320)}
                  alt={c.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                {c.soldOut && (
                  <span className="absolute top-2 left-2 bg-[#3D0A28] text-[#F5C542] text-[0.55rem] font-mono tracking-[0.14em] uppercase px-2 py-1" data-testid={`recent-buy-sold-${c.slug}`}>
                    Sold
                  </span>
                )}
              </div>
              <div className="mt-2 text-[0.72rem] font-body text-[#1A1A1A] truncate">{c.title}</div>
              <div className="text-[0.6rem] font-mono tracking-[0.08em] uppercase text-[#6E6E69] mt-0.5">{c.meta}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
